/**
 * kunstkaesten-cms – Instagram Exhibition Announcement Videos with CMS prefill
 *
 * Same animation behavior as kunstkaesten, but pre-fills copy and step 3 image
 * from the Nuxt CMS route at http://localhost:3333/api/some/aktuell.
 *
 * Any manually filled input overrides the CMS value.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate } from "animejs";
import { createTimeline } from "animejs/timeline";
import { COMPONENT_ORDER } from "../kunstkaesten/useCrossRotation";
import "../kunstkaesten/styles.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const FPS = 60;
const STILL_FRAME_MS = 5000;

const CMS_BASE = "https://kunstkaesten.hakanya.kim";
const CMS_URL = `${CMS_BASE}/api/some/aktuell`;

// Panels that always carry a scale(-1,-1) flip in addition to rotation
const FLIPPED_PANELS = new Set(["ausblick", "info"]);

// Base CSS rotations from styles.css (0 steps of rotation applied)
const BASE_ROTATIONS = { aktuell: 0, ausblick: 90, info: 0, archiv: 90 };

// Index of each named position in COMPONENT_ORDER
const POSITION_INDEX = { aktuell: 0, ausblick: 1, info: 2, archiv: 3 };

// Map video step names → rotation targets
const VIDEO_CONFIG = {
  1: { startAt: null, animateTo: null, panelKey: "aktuell" },
  2: { startAt: "aktuell", animateTo: "ausblick", panelKey: "ausblick" },
  3: { startAt: "ausblick", animateTo: "info", panelKey: "info" },
  4: { startAt: "info", animateTo: "archiv", panelKey: "archiv" },
};

const VIDEO_STEPS = Object.keys(VIDEO_CONFIG);

/** Return the absolute rotation for each panel when `steps` 90° rotations
 *  have been applied from the initial state. */
function rotationsAtStep(steps) {
  return Object.fromEntries(
    COMPONENT_ORDER.map((k) => [k, BASE_ROTATIONS[k] + steps * 90]),
  );
}

/** Format ISO date or timestamp → "3. März 2026" (German long form) */
function fmtGermanDate(isoOrDate) {
  if (!isoOrDate) return "";
  const dateStr = String(isoOrDate).split("T")[0];
  const [yyyy, mm, dd] = dateStr.split("-");
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  return new Intl.DateTimeFormat("de-CH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** Format ISO "YYYY-MM-DD" → "DD.MM." */
function fmtDay(iso) {
  if (!iso) return "";
  const [, mm, dd] = iso.split("-");
  return `${dd}.${mm}.`;
}

/** Build a date-span string like "01.02. – 31.05.26" from two ISO dates. */
function buildDateSpan(start, end) {
  if (!start && !end) return "";
  const s = fmtDay(start);
  const e = end ? `${fmtDay(end)}${String(end).slice(2, 4)}` : "";
  return `${s} – ${e}`;
}

// ─── Handler ──────────────────────────────────────────────────────────────────

export const handler = ({ inputs, mechanic }) => {
  // ── Fetch CMS data + convert images to blob URLs once on mount ─────────────
  const [cmsData, setCmsData] = useState(null);
  const [cmsFetched, setCmsFetched] = useState(false);
  const [cmsBlobUrls, setCmsBlobUrls] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      let data = null;
      try {
        const res = await fetch(CMS_URL);
        data = await res.json();
      } catch (_) {}

      if (!active) return;
      setCmsData(data);

      // Fetch images as blobs so canvas export can consume same-origin blob URLs.
      const blobs = {};
      const imgPaths = [
        ["image", data?.image],
        ["imageKuenstler", data?.imageKuenstler ?? data?.image],
      ];

      await Promise.all(
        imgPaths.map(async ([key, path]) => {
          if (!path) return;
          try {
            const r = await fetch(`${CMS_BASE}${path}`);
            if (r.ok) blobs[key] = URL.createObjectURL(await r.blob());
          } catch (_) {}
        }),
      );

      if (active) {
        setCmsBlobUrls(blobs);
        setCmsFetched(true);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  const {
    width,
    height,
    backgroundColor,
    textColor,
    inactiveBlur,
    inactiveOpacity,
    videoStep,
    label1,
    label2,
    label3,
    label4,
    exhibitionTitle,
    artistName,
    dateSpan,
    vernissageDate,
    vernissageTime,
    vernissageAddress,
    exhibitionTheme,
    artistDetails,
    imageAktuell,
    imageAusblick,
    imageInfo,
    imageArchiv,
  } = inputs;

  const config = VIDEO_CONFIG[videoStep];

  // ── Merge CMS data with manual input overrides (inputs win when non-empty) ─
  const resolvedArtistName = (artistName && artistName.trim())
    ? artistName
    : (cmsData?.artist ?? "");

  const resolvedExhibTitle = (exhibitionTitle && exhibitionTitle.trim())
    ? exhibitionTitle
    : (cmsData?.werktitel ?? cmsData?.title ?? "");

  const resolvedDateSpan = (dateSpan && dateSpan.trim())
    ? dateSpan
    : buildDateSpan(cmsData?.startDate, cmsData?.endDate);

  const resolvedVernissageDate = (vernissageDate && vernissageDate.trim())
    ? vernissageDate
    : fmtGermanDate(cmsData?.vernissage?.datum);

  const resolvedVernissageTime = (vernissageTime && vernissageTime.trim())
    ? vernissageTime
    : (cmsData?.vernissage?.uhrzeit ?? "");

  const resolvedVernissageAddress = (vernissageAddress && vernissageAddress.trim())
    ? vernissageAddress
    : (cmsData?.vernissage?.ort ?? "");

  const resolvedExhibTheme = (exhibitionTheme && exhibitionTheme.trim())
    ? exhibitionTheme
    : (cmsData?.kuenstlerBio ?? "");

  const resolvedArtistDetails = (artistDetails && artistDetails.trim())
    ? artistDetails
    : (cmsData?.description ?? "");

  // CMS images are pre-fetched as blob URLs to avoid export/canvas tainting.
  const cmsImageSrc = cmsBlobUrls.image ?? null;
  const cmsArtistImageSrc = cmsBlobUrls.imageKuenstler ?? cmsImageSrc;

  // Step mapping aligned with kunstkaesten-stills-cms:
  // 1=aktuell text-only, 2=ausblick optional manual image,
  // 3=info artist portrait from CMS (unless overridden), 4=archiv optional manual image.
  const panelImageUrls = useMemo(() => ({
    aktuell: imageAktuell ? URL.createObjectURL(imageAktuell) : null,
    ausblick: imageAusblick ? URL.createObjectURL(imageAusblick) : null,
    info: imageInfo ? URL.createObjectURL(imageInfo) : cmsArtistImageSrc,
    archiv: imageArchiv ? URL.createObjectURL(imageArchiv) : null,
  }), [imageAktuell, imageAusblick, imageInfo, imageArchiv, cmsArtistImageSrc]);

  const panelLabels = {
    aktuell: label1,
    ausblick: label2,
    info: label3,
    archiv: label4,
  };

  // ── Refs ────────────────────────────────────────────────────────────────────
  const panelRefs = useRef({});
  const setPanelRef = (key) => (el) => {
    panelRefs.current[key] = el;
  };

  const panelImgRefs = useRef({});
  const setPanelImgRef = (key) => (el) => {
    panelImgRefs.current[key] = el;
  };

  // Refs to image slot containers so images rotate with each panel.
  const imgSlotRefs = useRef({});
  const setImgSlotRef = (key) => (el) => {
    imgSlotRefs.current[key] = el;
  };

  const totalFrames = useRef(0);
  const tlRef = useRef(null);
  const isPlayingRef = useRef(false);
  const rafRef = useRef(null);
  const [frameCount, setFrameCount] = useState(0);

  const startDrawLoop = () => {
    isPlayingRef.current = true;
    const loop = () => {
      if (!isPlayingRef.current) return;
      setFrameCount((c) => c + 1);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const stopDrawLoop = () => {
    isPlayingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  // ── Timeline setup after CMS fetch resolved ─────────────────────────────────
  useEffect(() => {
    if (!cmsFetched || !config) return;

    const CONTENT_REVEAL_MS = 800;
    const BLUR_INACTIVE = `blur(${inactiveBlur}px)`;
    const OPACITY_INACTIVE = inactiveOpacity;

    const setInactiveTextStyles = (key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      const stepname = el.querySelector(".cross-stepname");
      const body = el.querySelector(".cross-body");

      if (stepname) {
        stepname.style.transition = "none";
        stepname.style.opacity = String(OPACITY_INACTIVE);
        stepname.style.filter = BLUR_INACTIVE;
      }

      if (body) {
        body.style.transition = "none";
        body.style.opacity = String(OPACITY_INACTIVE);
        body.style.filter = BLUR_INACTIVE;
      }
    };

    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      el.style.transition = "none";

      const stepname = el.querySelector(".cross-stepname");
      const body = el.querySelector(".cross-body");
      if (stepname) stepname.style.transition = "none";
      if (body) body.style.transition = "none";
    });

    // For image panels, text stays blurred/inactive permanently.
    const setImagePanelTextBlur = (key) => {
      setInactiveTextStyles(key);
    };

    if (!config.animateTo) {
      // Video 1
      const TOTAL_MS = 1150 + CONTENT_REVEAL_MS + STILL_FRAME_MS;
      totalFrames.current = Math.ceil((TOTAL_MS / 1000) * FPS);

      const tl = createTimeline({ autoplay: false });
      const staggerOffsets = { aktuell: 0, ausblick: 250, info: 500, archiv: 750 };

      COMPONENT_ORDER.forEach((key) => {
        if (panelImageUrls[key]) {
          setInactiveTextStyles(key);

          const el = panelRefs.current[key];
          const stepname = el?.querySelector(".cross-stepname");
          const body = el?.querySelector(".cross-body");

          tl.add(`.cross-${key}`, {
            opacity: [0, 1],
            duration: 400,
            ease: "outQuad",
          }, staggerOffsets[key]);

          if (stepname) {
            tl.add(stepname, {
              opacity: OPACITY_INACTIVE,
              filter: BLUR_INACTIVE,
              duration: 400,
              ease: "outQuad",
            }, staggerOffsets[key]);
          }

          if (body) {
            tl.add(body, {
              opacity: OPACITY_INACTIVE,
              filter: BLUR_INACTIVE,
              duration: 400,
              ease: "outQuad",
            }, staggerOffsets[key]);
          }
        } else {
          tl.add(`.cross-${key}`, {
            opacity: [0, OPACITY_INACTIVE],
            filter: BLUR_INACTIVE,
            duration: 400,
            ease: "outQuad",
          }, staggerOffsets[key]);
        }
      });

      if (panelImageUrls[config.panelKey]) {
        if (panelImgRefs.current[config.panelKey]) {
          tl.add(panelImgRefs.current[config.panelKey], {
            opacity: [0, 1],
            duration: CONTENT_REVEAL_MS,
            ease: "easeOutCubic",
          }, 1150);
        }
      } else {
        tl.add(`.cross-${config.panelKey}`, {
          opacity: [OPACITY_INACTIVE, 1],
          filter: [BLUR_INACTIVE, "blur(0px)"],
          duration: CONTENT_REVEAL_MS,
          ease: "easeOutCubic",
        }, 1150);
      }

      tlRef.current = tl;
      tl.seek(0);
      startDrawLoop();
    } else {
      // Videos 2-4
      const BEAT_MS = 200;
      const ROTATE_MS = 1000;
      const TOTAL_MS = BEAT_MS + ROTATE_MS + CONTENT_REVEAL_MS + STILL_FRAME_MS;
      totalFrames.current = Math.ceil((TOTAL_MS / 1000) * FPS);

      const startSteps = POSITION_INDEX[config.startAt];
      const startRots = rotationsAtStep(startSteps);
      const endRots = rotationsAtStep(startSteps + 1);

      COMPONENT_ORDER.forEach((key) => {
        const el = panelRefs.current[key];
        if (!el) return;

        const isFlipped = FLIPPED_PANELS.has(key);
        const isStartAt = key === config.startAt;

        if (panelImageUrls[key]) {
          animate(el, {
            opacity: 1,
            filter: "blur(0px)",
            rotate: startRots[key],
            ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
            duration: 0,
          });
          setImagePanelTextBlur(key);
        } else {
          animate(el, {
            opacity: isStartAt ? 1 : OPACITY_INACTIVE,
            filter: isStartAt ? "blur(0px)" : BLUR_INACTIVE,
            rotate: startRots[key],
            ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
            duration: 0,
          });
        }

        if (imgSlotRefs.current[key]) {
          animate(imgSlotRefs.current[key], {
            rotate: startRots[key],
            ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
            duration: 0,
          });
        }

        if (panelImageUrls[key] && panelImgRefs.current[key]) {
          animate(panelImgRefs.current[key], {
            opacity: isStartAt ? 1 : 0,
            duration: 0,
          });
        }
      });

      const tl = createTimeline({ autoplay: false });

      COMPONENT_ORDER.forEach((key) => {
        const el = panelRefs.current[key];
        if (!el) return;

        const isFlipped = FLIPPED_PANELS.has(key);
        const rotProps = {
          rotate: endRots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: ROTATE_MS,
          ease: "easeOutExpo",
        };

        tl.add(el, rotProps, BEAT_MS);
        if (imgSlotRefs.current[key]) {
          tl.add(imgSlotRefs.current[key], rotProps, BEAT_MS);
        }
      });

      if (panelImageUrls[config.startAt]) {
        if (panelImgRefs.current[config.startAt]) {
          tl.add(panelImgRefs.current[config.startAt], {
            opacity: [1, 0],
            duration: ROTATE_MS,
            ease: "easeInQuad",
          }, BEAT_MS);
        }
      } else {
        tl.add(`.cross-${config.startAt}`, {
          opacity: [1, OPACITY_INACTIVE],
          filter: ["blur(0px)", BLUR_INACTIVE],
          duration: ROTATE_MS,
          ease: "easeInQuad",
        }, BEAT_MS);
      }

      if (panelImageUrls[config.panelKey]) {
        if (panelImgRefs.current[config.panelKey]) {
          tl.add(panelImgRefs.current[config.panelKey], {
            opacity: [0, 1],
            duration: CONTENT_REVEAL_MS,
            ease: "easeOutCubic",
          }, BEAT_MS + ROTATE_MS);
        }
      } else {
        tl.add(`.cross-${config.panelKey}`, {
          opacity: [OPACITY_INACTIVE, 1],
          filter: [BLUR_INACTIVE, "blur(0px)"],
          duration: CONTENT_REVEAL_MS,
          ease: "easeOutCubic",
        }, BEAT_MS + ROTATE_MS);
      }

      tlRef.current = tl;
      tl.seek(0);
      startDrawLoop();
    }

    return () => stopDrawLoop();
  }, [cmsFetched]);

  // ── Per-frame capture ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!tlRef.current || totalFrames.current === 0) return;

    const elapsedMs = (frameCount / FPS) * 1000;
    tlRef.current.seek(elapsedMs);

    if (frameCount < totalFrames.current) {
      mechanic.frame();
    } else {
      stopDrawLoop();
      mechanic.done();
    }
  }, [frameCount]);

  // ── Per-panel body content mapped from resolved values ─────────────────────
  const panelContent = {
    aktuell: (
      <>
        <h1 className="cross-line">{resolvedArtistName}</h1>
        <h1 className="cross-line" style={{ fontStyle: "italic" }}>
          {resolvedExhibTitle}
        </h1>
        <h1 className="cross-line">{resolvedDateSpan}</h1>
      </>
    ),
    ausblick: (
      <>
        <h1 className="cross-line">{resolvedVernissageDate}</h1>
        <h1 className="cross-line">{resolvedVernissageAddress}</h1>
        <h1 className="cross-line">{resolvedVernissageTime}</h1>
      </>
    ),
    info: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {resolvedExhibTheme}
        </p>
      </>
    ),
    archiv: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {resolvedArtistDetails}
        </p>
      </>
    ),
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="cross-root"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor,
        color: textColor,
      }}
    >
      {COMPONENT_ORDER.map((key) => {
        const imgUrl = panelImageUrls[key];
        if (!imgUrl) return null;

        return (
          <div
            key={`img-${key}`}
            ref={setImgSlotRef(key)}
            className={`cross-image-slot cross-image-slot-${key}`}
          >
            <img
              ref={setPanelImgRef(key)}
              src={imgUrl}
              alt=""
              className="cross-panel-image"
              style={{ opacity: 0 }}
            />
          </div>
        );
      })}

      {COMPONENT_ORDER.map((key) => (
        <div key={key} className="cross-container">
          <div ref={setPanelRef(key)} className={`cross-panel cross-${key}`}>
            <h3 className="cross-stepname">{panelLabels[key]}</h3>
            <div className="cross-body">{panelContent[key]}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Inputs ───────────────────────────────────────────────────────────────────

export const inputs = {
  width: {
    type: "number",
    default: 1080,
    label: "Width (px)",
  },
  height: {
    type: "number",
    default: 1350,
    label: "Height (px)",
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#ffffff",
    label: "Background",
  },
  textColor: {
    type: "color",
    model: "hex",
    default: "#0a1ef5",
    label: "Text Color",
  },
  inactiveBlur: {
    type: "number",
    default: 9,
    min: 0,
    max: 40,
    slider: true,
    label: "Inactive Blur (px)",
  },
  inactiveOpacity: {
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    slider: true,
    label: "Inactive Opacity",
  },

  videoStep: {
    type: "text",
    default: VIDEO_STEPS[0],
    options: VIDEO_STEPS,
    label: "Video Step",
  },

  label1: {
    type: "text",
    default: "Neue Ausstellung",
    label: "Label - 1",
  },
  label2: {
    type: "text",
    default: "Vernissage",
    label: "Label - 2",
  },
  label3: {
    type: "text",
    default: "Zum Kuenstler",
    label: "Label - 3",
  },
  label4: {
    type: "text",
    default: "Zur Ausstellung",
    label: "Label - 4",
  },

  // Video 1 - Aktuell (auto-filled from CMS; override below)
  artistName: {
    type: "text",
    default: "",
    label: "Artist Name (override CMS)",
  },
  exhibitionTitle: {
    type: "text",
    default: "",
    label: "Werktitel (override CMS)",
  },
  dateSpan: {
    type: "text",
    default: "",
    label: "Date Span (override CMS)",
  },

  // Video 2 - Ausblick (Vernissage; auto-filled from CMS)
  vernissageDate: {
    type: "text",
    default: "",
    label: "Vernissage Date (override CMS)",
  },
  vernissageAddress: {
    type: "text",
    default: "",
    label: "Vernissage Address (override CMS)",
  },
  vernissageTime: {
    type: "text",
    default: "",
    label: "Vernissage Time (override CMS)",
  },

  // Video 3 - Info (auto-filled from CMS artist bio)
  exhibitionTheme: {
    type: "text",
    default: "",
    label: "Kuenstler:in Bio (override CMS)",
  },

  // Video 4 - Archiv (auto-filled from CMS description)
  artistDetails: {
    type: "text",
    default: "",
    label: "Kurzbeschreibung Werk (override CMS)",
  },

  // Step images (optional)
  imageAktuell: {
    type: "image",
    label: "Image - 1 (optional, sonst nur Text)",
  },
  imageAusblick: {
    type: "image",
    label: "Image - 2",
  },
  imageInfo: {
    type: "image",
    label: "Image - 3 (override CMS artist portrait)",
  },
  imageArchiv: {
    type: "image",
    label: "Image - 4",
  },
};

// ─── Presets ──────────────────────────────────────────────────────────────────

export const presets = {
  portrait: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Kunstkaesten CMS",
  animated: true,
  hideScaleToFit: true,
  initialScaleToFit: false,
  hideAutoRefresh: true,
};
